import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
const USER_CACHE = "fh_user_v1";
const remember = (u) => { try { u ? localStorage.setItem(USER_CACHE, JSON.stringify(u)) : localStorage.removeItem(USER_CACHE); } catch { /* storage full/blocked */ } };
const recall = () => { try { return JSON.parse(localStorage.getItem(USER_CACHE)); } catch { return null; } };

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            remember(data);
        } catch (e) {
            // No signal (no HTTP response) — keep the last signed-in user so the map still opens.
            if (!e?.response && recall()) { setUser({ ...recall(), _offline: true }); return; }
            setUser(null);
            remember(null);
        }
    }, []);

    useEffect(() => {
        const handler = () => refresh();
        window.addEventListener("user-updated", handler);
        return () => window.removeEventListener("user-updated", handler);
    }, [refresh]);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id first.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh().finally(() => setLoading(false));
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        remember(data.user);
        return data.user;
    };
    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        setUser(data.user);
        remember(data.user);
        return data.user;
    };
    const logout = async () => {
        remember(null);
        await api.post("/auth/logout").catch(() => {});
        setUser(null);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
